import './ServiceOrderBillingData.scss';

import { Flex, Form, Input, Radio, SelectProps, Tooltip, Typography } from 'antd';
import { FormProps } from 'antd/lib';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import AntdSelect from '@/components/general/AntdSelect/AntdSelect';
import FormattedInputNumber from '@/components/general/FormattedInputNumber';
import PaymentMethodCreateModal from '@/components/serviceOrder/PaymentMethodCreateModal';
import { useCountryContext } from '@/contexts/CountryContext';
import { PaymentMethod } from '@/model/General/PaymentMethod';
import { SERVICE_ORDER_TYPE } from '@/model/ServiceOrder/ServiceOrder';

interface ServiceOrderBillingDataProps {
  formProps?: FormProps;
  currencySelectProps?: SelectProps;
  assignTechnicianPrice?: (() => void) | undefined;
  paymentMethodSelectProps?: SelectProps;
  onCreatePaymentMethod?: (data: Partial<PaymentMethod>) => void;
  clientPaymentMethodSelectProps?: SelectProps;
  onCreateClientPaymentMethod?: (data: Partial<PaymentMethod>) => void;
  /** Instalación (ARG) sin precio por hora y horas cargados: precio fijo en su lugar. */
  useInstallationPrice?: boolean;
}

const ServiceOrderBillingData = (props: ServiceOrderBillingDataProps) => {
  const { isBrasilSelected } = useCountryContext();
  const {
    formProps,
    currencySelectProps,
    assignTechnicianPrice,
    paymentMethodSelectProps,
    onCreatePaymentMethod,
    clientPaymentMethodSelectProps,
    onCreateClientPaymentMethod,
    useInstallationPrice = false,
  } = props;
  const { t } = useTranslation();
  // Asignación de precios fijos para instalación
  const serviceType = Form.useWatch('type', formProps?.form);
  const currency = Form.useWatch('currency', formProps?.form);
  const technicianPay = Form.useWatch('technician_pay', formProps?.form);
  const customerInvoice = Form.useWatch('customer_invoice', formProps?.form);
  const currencySymbol = useMemo(
    () => currencySelectProps?.options?.find(c => c.value === currency)?.item?.simbolo ?? '$',
    [currency, currencySelectProps?.options],
  );
  const assignDefaultValues = useCallback(
    () =>
      formProps?.form?.setFieldsValue({
        // La moneda de instalación se fija sola en USD (ver efecto más abajo).
        ...(useInstallationPrice ? {} : { currency: 2, hourly_rate: 40 }),
        kilometres_rate: 1,
      }),
    [formProps, useInstallationPrice],
  );

  // Instalación: la moneda siempre es USD. El endpoint de currencies no soporta
  // filtros, así que se busca la opción por abreviación en la lista ya cargada
  // (no queda a elección del usuario).
  useEffect(() => {
    if (!useInstallationPrice) {
      return;
    }

    const usdCurrencyId = currencySelectProps?.options?.find(
      (option: any) => option.item?.abreviacion === 'USD',
    )?.value;

    if (usdCurrencyId != null && formProps?.form?.getFieldValue('currency') !== usdCurrencyId) {
      formProps?.form?.setFieldsValue({ currency: usdCurrencyId });
    }
  }, [useInstallationPrice, currencySelectProps?.options, formProps?.form]);

  return (
    <div className="ServiceOrderBillingData">
      <div className="ServiceOrderFormRow ServiceOrderFormRateRow">
        <Form.Item
          rules={[
            {
              required: true,
              message: t('El {{field}} es requerido', {
                field: t('Tipo de moneda'),
              }),
            },
          ]}
          label={t('Tipo de moneda')}
          name={'currency'}
        >
          <AntdSelect {...currencySelectProps} disabled={useInstallationPrice} />
        </Form.Item>
        {useInstallationPrice ? (
          <Form.Item
            rules={[
              {
                required: true,
                message: t('El {{field}} es requerido', {
                  field: t('Precio de la instalación'),
                }),
              },
            ]}
            label={t('Precio de la instalación')}
            name={'installation_price'}
          >
            <FormattedInputNumber style={{ width: '100%' }} suffix={currencySymbol} allowFloat />
          </Form.Item>
        ) : (
          <Form.Item
            rules={[
              {
                required: true,
                message: t('El {{field}} es requerido', {
                  field: t('Precio por hora'),
                }),
              },
            ]}
            label={t('Precio por hora')}
            name={'hourly_rate'}
          >
            <FormattedInputNumber
              style={{ width: '100%' }}
              suffix={`${currencySymbol}/hs`}
              allowFloat
            />
          </Form.Item>
        )}
        <Form.Item
          rules={[
            {
              required: true,
              message: t('El {{field}} es requerido', {
                field: t('Precio por kilómetro'),
              }),
            },
          ]}
          label={t('Precio por kilómetro')}
          name={'kilometres_rate'}
        >
          <FormattedInputNumber
            style={{ width: '100%' }}
            allowFloat
            suffix={`${currencySymbol}/km`}
          />
        </Form.Item>
      </div>
      <Flex align="end" justify="end" wrap gap={16} style={{ marginBottom: 16 }}>
        {serviceType == SERVICE_ORDER_TYPE.INSTALACION && (
          <div className="ServiceOrderLinkText" onClick={assignDefaultValues}>
            {t('Asignar valores por defecto')}
          </div>
        )}
        {!useInstallationPrice && (
          <Tooltip
            title={assignTechnicianPrice ? '' : t('El técnico no tiene los precios definidos')}
          >
            <Typography.Text
              disabled={!assignTechnicianPrice}
              className={assignTechnicianPrice ? `ServiceOrderLinkText` : ''}
              onClick={() => assignTechnicianPrice?.()}
            >
              {t('Asignar valores del técnico')}
            </Typography.Text>
          </Tooltip>
        )}
      </Flex>
      <div className="ServiceOrderFormRow">
        {!useInstallationPrice && (
          <Form.Item
            label={t('Horas')}
            name={'hours_estimated'}
            rules={[
              {
                required: true,
                message: t('Las {{field}} son obligatorias', { field: t('Horas').toLowerCase() }),
              },
            ]}
          >
            <FormattedInputNumber style={{ width: '100%' }} allowFloat suffix="hs" />
          </Form.Item>
        )}
        <Form.Item
          label={t('Kilómetros recorridos')}
          name={'kilometres_estimated'}
          rules={[
            {
              required: true,
              message: t('Los {{field}} son obligatorios', {
                field: t('Kilómetros recorridos').toLowerCase(),
              }),
            },
          ]}
        >
          <FormattedInputNumber style={{ width: '100%' }} allowFloat suffix="km" />
        </Form.Item>
      </div>
      <div className="ServiceOrderFormRow">
        <Form.Item
          label={t('Pagar servicio al técnico')}
          name={'technician_pay'}
          layout="horizontal"
          rules={[
            {
              required: true,
              message: t('Debe seleccionar una opción'),
            },
          ]}
        >
          <Radio.Group>
            <Radio value={true}>{t('Sí')}</Radio>
            <Radio value={false}>{t('No')}</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item
          label={t('Facturar al cliente')}
          name={'customer_invoice'}
          layout="horizontal"
          rules={[
            {
              required: true,
              message: t('Debe seleccionar una opción'),
            },
          ]}
        >
          <Radio.Group>
            <Radio value={true}>{t('Sí')}</Radio>
            <Radio value={false}>{t('No')}</Radio>
          </Radio.Group>
        </Form.Item>
      </div>
      <div className="ServiceOrderFormRow">
        {isBrasilSelected && technicianPay && (
          <Form.Item label={t('Método de pago al técnico')} required>
            <Flex align="center" gap={8}>
              <Form.Item
                name={'payment_method'}
                noStyle
                rules={[{ required: true, message: t('Debe seleccionar un método de pago') }]}
              >
                <AntdSelect {...paymentMethodSelectProps} />
              </Form.Item>
              <PaymentMethodCreateModal
                isBrasil={isBrasilSelected}
                onCreatePaymentMethod={onCreatePaymentMethod}
              />
            </Flex>
          </Form.Item>
        )}
        {isBrasilSelected && customerInvoice && (
          <Form.Item label={t('Método de cobro al cliente')} required>
            <Flex align="center" gap={8}>
              <Form.Item
                name={'payment_method_client'}
                noStyle
                rules={[{ required: true, message: t('Debe seleccionar un método de pago') }]}
              >
                <AntdSelect {...clientPaymentMethodSelectProps} />
              </Form.Item>
              <PaymentMethodCreateModal
                isBrasil={isBrasilSelected}
                onCreatePaymentMethod={onCreateClientPaymentMethod}
              />
            </Flex>
          </Form.Item>
        )}
      </div>

      <div className="ServiceOrderFormRow">
        <Form.Item
          label={t('Horas cubiertas por Acronex')}
          name={'hours_covered'}
          rules={[
            {
              required: true,
              message: t('Las {{field}} son obligatorias', {
                field: t('Horas cubiertas por Acronex').toLowerCase(),
              }),
            },
          ]}
        >
          <FormattedInputNumber style={{ width: '100%' }} allowFloat suffix="hs" />
        </Form.Item>
        <Form.Item
          label={t('Kilómetros cubiertos por Acronex')}
          name={'kilometres_covered'}
          rules={[
            {
              required: true,
              message: t('Los {{field}} son obligatorios', {
                field: t('Kilómetros cubiertos por Acronex').toLowerCase(),
              }),
            },
          ]}
        >
          <FormattedInputNumber style={{ width: '100%' }} allowFloat suffix="km" />
        </Form.Item>
      </div>
      <Form.Item
        label={t('Comentarios / Gastos adicionales')}
        name={'aditional_comment'}
        layout="vertical"
      >
        <Input.TextArea autoSize={{ minRows: 2, maxRows: 6 }} />
      </Form.Item>
    </div>
  );
};

export default ServiceOrderBillingData;
